import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MembersClass } from '../../../../../models/interfaces/members_class.interface';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomFile } from '../../../../../models/interfaces/custom_file.interface';
import { MembersCategory } from '../../../../../models/interfaces/members_category.interface';
import { RemoteFile } from '../../../../../models/classes/remote_file.model';
import { MembersLevel } from '../../../../../models/interfaces/members_level.interface';
import { Subject, firstValueFrom, pairwise, startWith, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { UtilsService } from '../../../../../shared/utils/utils.service';
import { cloneDeep, difference } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { EnfafDropzoneComponent } from '../../../../../components/enfaf-dropzone/enfaf-dropzone.component';
import { EnfafRemoteAttachmentsComponent } from '../../../../../components/enfaf-remote-attachments/enfaf-remote-attachments.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
    selector: 'app-modal-edit-members-class',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        EnfafDropzoneComponent,
        EnfafRemoteAttachmentsComponent,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatSlideToggleModule
    ],
    templateUrl: './modal-edit-members-class.component.html',
    styleUrl: './modal-edit-members-class.component.scss'
})
export class ModalEditMembersClassComponent implements OnInit, OnDestroy {
  isLoadingClass = true;
  receivedClass!: MembersClass;
  classForm: FormGroup = new FormGroup({});
  editMode = false;
  showCloseAlert = false;
  isSavingClass = false;
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;
  currentThumbnail!: string;
  filesToUpload: Array<CustomFile> = [];
  categories: Array<MembersCategory> = [];
  selectedCategories: Array<MembersCategory> = [];
  currentAttachments: Array<RemoteFile> = [];
  isDeletingRemoteAttachment = false;
  levels: Array<MembersLevel> = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditMembersClassComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.categories = receivedData?.categories || [];
    if (!this.categories.length) return this.closeModal();

    this.levels = receivedData?.levels || [];
    const membersClassId = receivedData?.membersClassId;
    if (membersClassId) {
      this.fetchMembersClass(membersClassId);
    } else {
      this.isLoadingClass = false;
      this.initForm();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }
    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async fetchMembersClass(membersClassId: string): Promise<void> {
    try {
      const params = { membersClassId };
      const response = await firstValueFrom(this.api.fetchMembersClass(params));
      this.isLoadingClass = false;
      this.receivedClass = response.class;
      this.initForm();
    } catch (errorResponse: any) {
      this.isLoadingClass = false;
      this.initForm();
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error buscando clase');
    }
  }

  initForm(): void {
    if (this.receivedClass) {
      this.editMode = true;
      this.currentThumbnail = this.receivedClass.thumbnailUrl;
      this.currentAttachments = this.receivedClass.attachments;
    }

    this.classForm = new FormGroup({
      membersClassId: new FormControl(this.receivedClass?.membersClassId || uuidv4(), Validators.required),
      name: new FormControl(this.receivedClass?.name || null, Validators.required),
      description: new FormControl(this.receivedClass?.description || null, Validators.required),
      videoUrl: new FormControl(this.receivedClass?.videoUrl || null, Validators.required),
      categories: new FormControl([], Validators.required),
      levels: new FormControl([], Validators.required),
      positions: new FormArray([], Validators.required),
      hidden: new FormControl(this.receivedClass?.hidden || false, Validators.required)
    });

    if (this.receivedClass) {
      // Se actualizan las categorías
      const categoryIds: string[] = [];
      const currentPositions = this.classForm.get('positions') as FormArray;
      this.receivedClass.positions.forEach((receivedPosition) => {
        categoryIds.push(receivedPosition.categoryId);
        // Se añade la categoría al listado de categorías seleccionadas
        const categoryFound = this.categories.find((category) => category.membersCategoryId === receivedPosition.categoryId);
        if (categoryFound) {
          const categoryCopy = cloneDeep(categoryFound);
          this.selectedCategories.push(categoryCopy);
        }
        const newPositionFormGroup = new FormGroup({
          categoryId: new FormControl(receivedPosition.categoryId, Validators.required),
          position: new FormControl(receivedPosition.position, Validators.required)
        });
        currentPositions.push(newPositionFormGroup);
      });
      // Se actualizan los niveles
      const levelIds: string[] = [];
      this.receivedClass.levels.forEach((receivedLevelId) => {
        levelIds.push(receivedLevelId);
      });
      this.classForm.patchValue({ categories: categoryIds, levels: levelIds }, { emitEvent: false });
    }
    this.listenForChanges();
  }

  listenForChanges(): void {
    this.classForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
    });

    this.classForm.controls.categories.valueChanges
      .pipe(startWith(this.classForm.controls.categories.value), pairwise(), takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        // Se verifica el cambio de selección en categorías
        const prevCategories = value[0];
        const newCategories = value[1];
        const currentPositions = this.classForm.get('positions') as FormArray;
        if (newCategories.length > prevCategories.length) {
          const categoriesDiff: Array<string> = difference(newCategories, prevCategories);
          const lastCategoryIdAdded = categoriesDiff[0];
          // Se añade la categoría al listado de categorías seleccionadas
          const category = this.categories.find((category) => category.membersCategoryId === lastCategoryIdAdded);
          if (category) {
            const categoryCopy = cloneDeep(category);
            this.selectedCategories.push(categoryCopy);
          }
          const diff = newCategories.length - prevCategories.length;
          for (let i = 0; i < diff; i++) {
            const newPositionFormGroup = new FormGroup({
              categoryId: new FormControl(lastCategoryIdAdded, Validators.required),
              position: new FormControl(0, Validators.required)
            });
            currentPositions.push(newPositionFormGroup, { emitEvent: false });
          }
        } else if (newCategories.length < prevCategories.length) {
          for (const prevCategoryId of prevCategories) {
            const categoryIdExists = newCategories.find((newCategoryId: string) => newCategoryId === prevCategoryId);
            if (!categoryIdExists) {
              const positionControlToRemove = currentPositions.controls.findIndex((control) => control.value.categoryId === prevCategoryId);
              currentPositions.removeAt(positionControlToRemove, { emitEvent: false });
              const selectedCategoryIndexToRemove = this.selectedCategories.findIndex(
                (selectedCategory) => selectedCategory.membersCategoryId === prevCategoryId
              );
              if (selectedCategoryIndexToRemove > -1) {
                this.selectedCategories.splice(selectedCategoryIndexToRemove, 1);
              }
            }
          }
        }
      });
  }

  positionsControls(): AbstractControl<any, any>[] {
    const items = this.classForm.controls.positions as FormArray;
    return items.controls;
  }

  getCategoryName(membersCategoryId: string): string {
    const category = this.selectedCategories.find((selectedCategory) => selectedCategory.membersCategoryId === membersCategoryId);
    return category ? category.name : 'No se encontró categoría';
  }

  getCategoryClasses(membersCategoryId: string): Array<MembersClass> {
    const categoryIndex = this.categories.findIndex((category) => category.membersCategoryId === membersCategoryId);
    if (categoryIndex === -1) return [];
    return this.categories[categoryIndex].classes;
  }

  getPosition(categoryId: string): number {
    if (!this.receivedClass) return -1;

    const categoryIndex = this.receivedClass.positions.findIndex((position) => position.categoryId === categoryId);
    if (categoryIndex === -1) return -1;

    return this.receivedClass.positions[categoryIndex].position;
  }

  async onSelectImage(event: any) {
    try {
      const imgFile: File = event.target.files && event.target.files[0];
      if (!imgFile) return;
      if (imgFile.size > this.utils.maxImageSize) {
        (document.getElementById('classThumbnail') as HTMLInputElement).value = '';
        const fileName = imgFile.name;
        const errorMessage = `La imagen '${fileName}' no puede exceder los ${this.utils.maxImageSize / this.utils.oneMegaByte} MB\n`;
        this.toast.presentErrorToast(errorMessage);
        return;
      }
      this.imageToUploadSrc = await this.utils.previewImageFile(imgFile);
      if (!this.imageToUploadSrc) return;
      this.imageToUpload = imgFile;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      this.toast.presentErrorToast('Hubo un error adjuntando imagen');
    }
  }

  onDroppedAttachments(files: Array<CustomFile>): void {
    if (!files?.length) return;
    for (const file of files) {
      file.name = this.utils.generateFileName(file.name);
    }
    this.filesToUpload = files;
  }

  async saveClass(): Promise<void> {
    try {
      this.isSavingClass = true;
      const classFormData = new FormData();
      const formValue = this.classForm.value;
      classFormData.append('form', JSON.stringify(formValue));

      if (this.imageToUpload) {
        classFormData.append('thumbnail', this.imageToUpload, this.imageToUpload.name);
      }

      if (this.filesToUpload.length) {
        for (const [fileIndex, fileValue] of this.filesToUpload.entries()) {
          classFormData.append(`attachment_${fileIndex}`, fileValue.file, fileValue.name);
        }
      }

      const response = await firstValueFrom(this.api.saveMembersClass(classFormData));
      this.isSavingClass = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ course: response.course, categories: response.categories });
    } catch (errorResponse: any) {
      this.isSavingClass = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando clase');
    }
  }

  async onDeleteRemoteAttachment(result: { attachment: RemoteFile; index: number }): Promise<void> {
    try {
      const { attachment, index } = result;
      this.isDeletingRemoteAttachment = true;
      const membersClassId = this.classForm.value.membersClassId;
      const body = {
        membersClassId,
        attachment
      };
      const response = await firstValueFrom(this.api.deleteMembersClassAttachment(body));
      this.isDeletingRemoteAttachment = false;
      this.currentAttachments.splice(index, 1);
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isDeletingRemoteAttachment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando adjunto');
    }
  }
}
